import { AxiosResponse } from 'axios';

import { getRecordingsUrl } from 'config/api.ts';

import { GetRecordingsResponse } from 'store/railsBuilder/reducer.types.ts';
import { FetchRecordingsSortType } from 'store/recordings/actions.types.ts';
import { AsyncActionMetaOptions } from 'store/types.ts';

import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import NagraInstance from 'utils/axios/nagra/DefaultInstance.ts';
import { delayRetry } from 'utils/axios/nagra/utils.ts';

type Params = {
    id: string | undefined;
} & Pick<
    AsyncActionMetaOptions,
    'abortController' | 'autoRetry' | 'retryCount' | 'retryDuration'
>;

async function requestRecordingDetailsData({
    id,
    autoRetry,
    abortController,
    retryCount = 5,
    retryDuration = 5 * 60 * 1000,
}: Params): Promise<AxiosResponse<GetRecordingsResponse> | undefined> {
    try {
        return await NagraInstance.get(
            getRecordingsUrl({
                filter: { eventId: id },
                sort: FetchRecordingsSortType.CHRONOLOGICAL_DESC,
            }),
            { signal: abortController?.signal },
        );
    } catch (error) {
        if (autoRetry && isErrorNeedsRetry(error)) {
            for (let i = 1; i <= retryCount; i += 1) {
                if (abortController?.aborted) {
                    return undefined;
                }

                // eslint-disable-next-line no-await-in-loop
                await delayRetry(i, retryCount, retryDuration);

                try {
                    // eslint-disable-next-line no-await-in-loop
                    return await NagraInstance.get(
                        getRecordingsUrl({
                            filter: { eventId: id },
                            sort: FetchRecordingsSortType.CHRONOLOGICAL_DESC,
                        }),
                        { signal: abortController?.signal },
                    );
                } catch (retryError) {
                    if (i === retryCount) {
                        return undefined;
                    }
                }
            }
        }

        return undefined;
    }
}

export default requestRecordingDetailsData;
